import { useState, useRef, useCallback } from 'react';
import * as tus from 'tus-js-client';
import { Image, Video, Upload, X, Loader2, Trash2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface TaskAttachment {
  url: string;
  name: string;
  type: 'image' | 'video' | 'file';
  vimeoId?: string;
}

interface TaskAttachmentsProps {
  attachments: TaskAttachment[];
  onChange: (attachments: TaskAttachment[]) => void;
}

const MAX_IMAGE_SIZE = 20 * 1024 * 1024; // 20 MB
const MAX_VIDEO_SIZE = 2 * 1024 * 1024 * 1024; // 2 GB
const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm', 'video/x-matroska'];

export function TaskAttachments({ attachments, onChange }: TaskAttachmentsProps) {
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoStatus, setVideoStatus] = useState('');
  const imageInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);
  const tusRef = useRef<tus.Upload | null>(null);

  const handleImageUpload = useCallback(async (file: File) => {
    if (!IMAGE_TYPES.includes(file.type)) {
      toast.error('Nur JPG, PNG, GIF oder WebP erlaubt');
      return;
    }
    if (file.size > MAX_IMAGE_SIZE) {
      toast.error('Bild ist zu groß (max. 20 MB)');
      return;
    }

    setIsUploadingImage(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Nicht angemeldet');

      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const path = `task-attachments/${session.user.id}/${crypto.randomUUID()}.${ext}`;

      const { error } = await supabase.storage
        .from('community-uploads')
        .upload(path, file);
      if (error) throw error;

      const { data } = supabase.storage
        .from('community-uploads')
        .getPublicUrl(path);

      const newAttachment: TaskAttachment = {
        url: data.publicUrl,
        name: file.name,
        type: 'image',
      };
      onChange([...attachments, newAttachment]);
      toast.success('Bild hochgeladen');
    } catch (err) {
      console.error('Image upload error:', err);
      toast.error('Bild-Upload fehlgeschlagen');
    } finally {
      setIsUploadingImage(false);
    }
  }, [attachments, onChange]);

  const handleVideoUpload = useCallback(async (file: File) => {
    if (!VIDEO_TYPES.includes(file.type)) {
      toast.error('Nur MP4, MOV, WebM oder MKV erlaubt');
      return;
    }
    if (file.size > MAX_VIDEO_SIZE) {
      toast.error('Video ist zu groß (max. 2 GB)');
      return;
    }

    setIsUploadingVideo(true);
    setVideoProgress(0);
    setVideoStatus('Erstelle Upload…');

    try {
      const { data, error: fnError } = await supabase.functions.invoke('create-vimeo-upload', {
        body: { fileName: file.name, fileSize: file.size, context: 'task' },
      });

      if (fnError || !data?.uploadLink) {
        throw new Error(fnError?.message || 'Upload konnte nicht erstellt werden');
      }

      setVideoStatus('Lade Video hoch…');

      await new Promise<void>((resolve, reject) => {
        const upload = new tus.Upload(file, {
          uploadUrl: data.uploadLink,
          retryDelays: [0, 1000, 3000, 5000],
          metadata: { filename: file.name, filetype: file.type },
          onError: (err) => reject(err),
          onProgress: (bytesUploaded, bytesTotal) => {
            const pct = Math.round((bytesUploaded / bytesTotal) * 100);
            setVideoProgress(pct);
            setVideoStatus(`Lade hoch… ${pct}%`);
          },
          onSuccess: () => {
            tusRef.current = null;
            resolve();
          },
        });
        tusRef.current = upload;
        upload.start();
      });

      const vimeoId = data.videoId as string;
      const newAttachment: TaskAttachment = {
        url: `https://vimeo.com/${vimeoId}`,
        name: file.name,
        type: 'video',
        vimeoId,
      };
      onChange([...attachments, newAttachment]);
      toast.success('Video hochgeladen – wird von Vimeo verarbeitet');
    } catch (err) {
      console.error('Video upload error:', err);
      toast.error('Video-Upload fehlgeschlagen');
    } finally {
      setIsUploadingVideo(false);
      setVideoProgress(0);
      setVideoStatus('');
    }
  }, [attachments, onChange]);

  const handleRemove = (index: number) => {
    onChange(attachments.filter((_, i) => i !== index));
  };

  const handleCancelVideo = () => {
    tusRef.current?.abort();
    tusRef.current = null;
    setIsUploadingVideo(false);
    setVideoProgress(0);
    setVideoStatus('');
  };

  return (
    <div className="space-y-3">
      <Label>Anhänge</Label>

      {attachments.length > 0 && (
        <div className="space-y-2">
          {attachments.map((att, idx) => (
            <div key={idx} className="flex items-center gap-2 p-2 border rounded-lg bg-muted/30 text-sm">
              {att.type === 'image' ? (
                <Image className="h-4 w-4 text-blue-500 shrink-0" />
              ) : (
                <Video className="h-4 w-4 text-purple-500 shrink-0" />
              )}
              <span className="flex-1 truncate">{att.name}</span>
              {att.type === 'image' && (
                <a href={att.url} target="_blank" rel="noopener noreferrer" className="shrink-0">
                  <ExternalLink className="h-3.5 w-3.5 text-muted-foreground hover:text-foreground" />
                </a>
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-6 w-6 shrink-0"
                onClick={() => handleRemove(idx)}
              >
                <Trash2 className="h-3 w-3 text-destructive" />
              </Button>
            </div>
          ))}
        </div>
      )}

      {attachments.some(a => a.type === 'image') && (
        <div className="flex flex-wrap gap-2">
          {attachments.filter(a => a.type === 'image').map((att, idx) => (
            <a key={idx} href={att.url} target="_blank" rel="noopener noreferrer">
              <img
                src={att.url}
                alt={att.name}
                className="h-16 w-16 object-cover rounded border"
              />
            </a>
          ))}
        </div>
      )}

      {attachments.filter(a => a.type === 'video' && a.vimeoId).map((att, idx) => {
        const [vId, vHash] = (att.vimeoId ?? '').split('/');
        const src = vHash
          ? `https://player.vimeo.com/video/${vId}?h=${vHash}`
          : `https://player.vimeo.com/video/${vId}`;
        return (
          <div key={`video-${idx}`} className="space-y-1">
            <p className="text-xs text-muted-foreground truncate">{att.name}</p>
            <div className="aspect-video rounded-lg overflow-hidden bg-black border">
              <iframe
                src={src}
                className="w-full h-full"
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
                title={att.name}
              />
            </div>
          </div>
        );
      })}

      {isUploadingVideo && (
        <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
          <div className="flex items-center justify-between">
            <p className="text-sm font-medium">{videoStatus}</p>
            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={handleCancelVideo}>
              <X className="h-3 w-3" />
            </Button>
          </div>
          <Progress value={videoProgress} className="h-2" />
        </div>
      )}

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isUploadingImage || isUploadingVideo}
          onClick={() => imageInputRef.current?.click()}
          className="gap-1.5"
        >
          {isUploadingImage ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Image className="h-3.5 w-3.5" />
          )}
          Foto
        </Button>

        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={isUploadingImage || isUploadingVideo}
          onClick={() => videoInputRef.current?.click()}
          className="gap-1.5"
        >
          <Video className="h-3.5 w-3.5" />
          Video
        </Button>
      </div>

      <input
        ref={imageInputRef}
        type="file"
        className="hidden"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleImageUpload(f);
          if (imageInputRef.current) imageInputRef.current.value = '';
        }}
      />
      <input
        ref={videoInputRef}
        type="file"
        className="hidden"
        accept="video/*"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleVideoUpload(f);
          if (videoInputRef.current) videoInputRef.current.value = '';
        }}
      />
    </div>
  );
}
